import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Plus, Pencil, Trash2, X, Package } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = ["Overview", "Orders", "Products", "Inventory", "Discounts", "Lookbook", "Customers"];
const PIPELINE = ["ordered", "printing", "embroidery", "qc", "dispatched", "delivered", "cancelled"];
const CATEGORIES = ["Tees", "Hoodies", "Outerwear", "Headwear"];

const EMPTY_PRODUCT = { name: "", category: "Tees", description: "", price: "", compare_at: "", image: "", colors: "Black", sizes: "S,M,L,XL,2XL", customizable: true, featured: false };

function Kpi({ label, value, testid, accent }) {
  return (
    <div className="border border-white/10 bg-card p-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
      <p className={`font-display text-3xl mt-2 ${accent ? "text-kinetic" : "text-white"}`}>{value}</p>
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!data) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid="analytics-loading">Loading analytics…</p>;
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-revenue" label="Total Revenue" value={`$${data.total_revenue.toLocaleString()}`} accent />
        <Kpi testid="kpi-paid" label="Collected" value={`$${data.paid_revenue.toLocaleString()}`} />
        <Kpi testid="kpi-active-orders" label="Active Orders" value={data.active_orders} />
        <Kpi testid="kpi-customers" label="Customers" value={data.customers} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-white/10 bg-card p-5" data-testid="revenue-chart">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Revenue — last 14 days</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.revenue_by_day}>
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={50} />
              <Tooltip contentStyle={{ background: "#121215", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#FF3E15" fill="#FF3E15" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-white/10 bg-card p-5" data-testid="status-chart">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Orders by status</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={Object.entries(data.orders_by_status).map(([status, count]) => ({ status, count }))}>
              <XAxis dataKey="status" tick={{ fill: "#71717a", fontSize: 9, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#121215", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Bar dataKey="count" fill="#FF3E15" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-card p-5" data-testid="top-products">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Top products</p>
          {data.top_products.length === 0 && <p className="font-mono text-xs text-zinc-600">No sales yet.</p>}
          {data.top_products.map((p, i) => (
            <div key={p.name} className="flex justify-between font-mono text-xs uppercase tracking-widest py-2 border-b border-white/5 last:border-0">
              <span className="text-zinc-300"><span className="text-kinetic mr-2">{String(i + 1).padStart(2, "0")}</span>{p.name}</span>
              <span className="text-zinc-500">{p.qty} units</span>
            </div>
          ))}
        </div>
        <div className="border border-white/10 bg-card p-5" data-testid="low-stock-alerts">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Stock alerts</p>
          {data.low_stock.length === 0 && <p className="font-mono text-xs text-zinc-600">All stocked up.</p>}
          {data.low_stock.map((p) => (
            <div key={p.name} className="flex justify-between font-mono text-xs uppercase tracking-widest py-2 border-b border-white/5 last:border-0">
              <span className="text-zinc-300">{p.name}</span>
              <span className="text-kinetic">{Object.entries(p.stock).map(([s, n]) => `${s}:${n}`).join(" ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/orders/${id}`, patch);
      toast.success("Order updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!orders) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading orders…</p>;
  return (
    <div className="border border-white/10 bg-card overflow-x-auto" data-testid="admin-orders-table">
      <table className="w-full text-left font-mono text-xs">
        <thead>
          <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
            <th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Items</th><th className="p-4">Total</th><th className="p-4">Payment</th><th className="p-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-600 uppercase tracking-widest" data-testid="orders-empty-row">No orders yet</td></tr>}
          {orders.map((o) => (
            <tr key={o.id || o.order_number} className="border-b border-white/5 hover:bg-cardhover transition-colors" data-testid={`admin-order-row-${o.order_number}`}>
              <td className="p-4 text-kinetic">{o.order_number}<br /><span className="text-zinc-600 text-[10px]">{new Date(o.created_at).toLocaleDateString()}</span></td>
              <td className="p-4 text-zinc-300">{o.customer?.name}<br /><span className="text-zinc-600 text-[10px]">{o.customer_email}</span></td>
              <td className="p-4 text-zinc-400">
                {o.items.map((i) => `${i.qty}× ${i.name} (${i.size})`).join(", ")}
                {o.items.some((i) => i.customization?.artwork_path) && (
                  <div className="flex gap-2 mt-2">
                    {o.items.filter((i) => i.customization?.artwork_path).map((i, idx) => (
                      <a key={idx} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${i.customization.artwork_path}`} target="_blank" rel="noreferrer" data-testid={`order-artwork-${o.order_number}-${idx}`} title="View artwork">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${i.customization.artwork_path}`} alt="Custom artwork" className="w-8 h-8 object-contain border border-kinetic/40 bg-black" />
                      </a>
                    ))}
                  </div>
                )}
              </td>
              <td className="p-4 text-white">${o.total.toFixed(2)}</td>
              <td className="p-4">
                <select
                  data-testid={`order-payment-${o.order_number}`}
                  value={o.payment_status}
                  onChange={(e) => update(o.id, { payment_status: e.target.value })}
                  className="bg-surface border border-white/10 px-2 py-1.5 text-[11px] uppercase text-zinc-300 focus:outline-none focus:ring-1 focus:ring-kinetic"
                >
                  {["pending", "paid", "invoice_pending", "failed", "expired"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </td>
              <td className="p-4">
                <select
                  data-testid={`order-status-${o.order_number}`}
                  value={o.status}
                  onChange={(e) => update(o.id, { status: e.target.value })}
                  className="bg-surface border border-white/10 px-2 py-1.5 text-[11px] uppercase text-zinc-300 focus:outline-none focus:ring-1 focus:ring-kinetic"
                >
                  {PIPELINE.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product?.id;
  const [form, setForm] = useState(() => product
    ? { ...product, colors: product.colors.join(","), sizes: product.sizes.join(","), compare_at: product.compare_at || "" }
    : EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, category: form.category, description: form.description,
      price: parseFloat(form.price), compare_at: form.compare_at ? parseFloat(form.compare_at) : null,
      image: form.image, colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      customizable: !!form.customizable, featured: !!form.featured,
      stock: isEdit ? undefined : {},
    };
    if (isEdit) delete payload.stock;
    try {
      if (isEdit) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-surface border border-white/10 p-8 max-h-[90vh] overflow-y-auto" data-testid="product-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display text-2xl uppercase">{isEdit ? "Edit product" : "New product"}</h3>
          <button onClick={onClose} data-testid="product-modal-close" className="text-zinc-500 hover:text-kinetic"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <input data-testid="product-name-input" required value={form.name} onChange={set("name")} placeholder="NAME" className="input-dark font-mono uppercase text-xs tracking-widest" />
          <select data-testid="product-category-select" value={form.category} onChange={set("category")} className="input-dark font-mono uppercase text-xs tracking-widest">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="product-price-input" required type="number" step="0.01" min="0" value={form.price} onChange={set("price")} placeholder="PRICE (USD)" className="input-dark font-mono uppercase text-xs tracking-widest" />
          <input data-testid="product-compare-input" type="number" step="0.01" min="0" value={form.compare_at} onChange={set("compare_at")} placeholder="COMPARE-AT (OPTIONAL)" className="input-dark font-mono uppercase text-xs tracking-widest" />
          <input data-testid="product-image-input" value={form.image} onChange={set("image")} placeholder="IMAGE URL" className="input-dark font-mono text-xs sm:col-span-2" />
          <input data-testid="product-colors-input" value={form.colors} onChange={set("colors")} placeholder="COLORS (COMMA SEPARATED)" className="input-dark font-mono uppercase text-xs tracking-widest" />
          <input data-testid="product-sizes-input" value={form.sizes} onChange={set("sizes")} placeholder="SIZES (COMMA SEPARATED)" className="input-dark font-mono uppercase text-xs tracking-widest" />
          <textarea data-testid="product-description-input" value={form.description} onChange={set("description")} placeholder="DESCRIPTION" rows={3} className="input-dark text-sm sm:col-span-2" />
          <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-400">
            <input data-testid="product-customizable-check" type="checkbox" checked={!!form.customizable} onChange={set("customizable")} className="accent-[#FF3E15] w-4 h-4" /> Customizable
          </label>
          <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-400">
            <input data-testid="product-featured-check" type="checkbox" checked={!!form.featured} onChange={set("featured")} className="accent-[#FF3E15] w-4 h-4" /> Featured
          </label>
          <button data-testid="product-save-button" disabled={saving} className="btn-kinetic sm:col-span-2 disabled:opacity-50">{saving ? "Saving…" : isEdit ? "Save changes" : "Create product"}</button>
        </form>
      </div>
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | product
  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!products) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading products…</p>;
  return (
    <div data-testid="admin-products">
      <button data-testid="add-product-button" onClick={() => setEditing({})} className="btn-kinetic mb-6"><Plus size={14} /> New product</button>
      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left font-mono text-xs">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="p-4">Product</th><th className="p-4">Category</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Flags</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-cardhover transition-colors" data-testid={`admin-product-row-${p.id}`}>
                <td className="p-4 text-zinc-200 flex items-center gap-3"><img src={p.image} alt="" className="w-9 h-11 object-cover" />{p.name}</td>
                <td className="p-4 text-zinc-400 uppercase">{p.category}</td>
                <td className="p-4 text-white">${p.price.toFixed(2)}</td>
                <td className="p-4 text-zinc-400">{Object.values(p.stock || {}).reduce((a, b) => a + b, 0)} units</td>
                <td className="p-4">
                  {p.featured && <span className="text-volt mr-2">FEATURED</span>}
                  {p.customizable && <span className="text-kinetic">CUSTOM</span>}
                </td>
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="text-zinc-400 hover:text-kinetic transition-colors"><Pencil size={15} /></button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="text-zinc-400 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing !== null && <ProductModal product={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Inventory() {
  const [products, setProducts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const load = () => api.get("/products").then(({ data }) => {
    setProducts(data);
    setDrafts(Object.fromEntries(data.map((p) => [p.id, { ...p.stock }])));
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    try {
      await api.patch(`/admin/products/${p.id}/stock`, { stock: drafts[p.id] });
      toast.success(`${p.name} stock saved`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!products) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading inventory…</p>;
  return (
    <div className="space-y-4" data-testid="admin-inventory">
      {products.map((p) => (
        <div key={p.id} className="border border-white/10 bg-card p-5 flex flex-wrap items-center gap-4" data-testid={`inventory-row-${p.id}`}>
          <img src={p.image} alt="" className="w-10 h-12 object-cover" />
          <div className="w-48">
            <p className="font-bold text-sm uppercase">{p.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.category}</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-1">
            {p.sizes.map((s) => {
              const val = drafts[p.id]?.[s] ?? 0;
              const low = val <= 5;
              return (
                <label key={s} className="flex flex-col items-center gap-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s}</span>
                  <input
                    data-testid={`stock-input-${p.id}-${s}`}
                    type="number" min="0"
                    value={val}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: { ...d[p.id], [s]: parseInt(e.target.value || "0", 10) } }))}
                    className={`w-16 bg-surface border px-2 py-1.5 font-mono text-xs text-center focus:outline-none focus:ring-1 focus:ring-kinetic ${low ? "border-kinetic text-kinetic" : "border-white/10 text-zinc-200"}`}
                  />
                </label>
              );
            })}
          </div>
          <button data-testid={`stock-save-${p.id}`} onClick={() => save(p)} className="btn-ghost !px-5 !py-2.5"><Package size={13} /> Save</button>
        </div>
      ))}
    </div>
  );
}

function Discounts() {
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState({ code: "", type: "percent", value: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/discounts").then(({ data }) => setCodes(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/discounts", { code: form.code, type: form.type, value: parseFloat(form.value) });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: "", type: "percent", value: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (code) => {
    try { await api.patch(`/admin/discounts/${code}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete ${code}?`)) return;
    try { await api.delete(`/admin/discounts/${code}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!codes) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading codes…</p>;
  return (
    <div data-testid="admin-discounts">
      <form onSubmit={create} className="border border-white/10 bg-card p-5 mb-6 grid sm:grid-cols-[1fr_auto_auto_auto] gap-3">
        <input data-testid="discount-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE (E.G. KILGORE20)" className="input-dark font-mono uppercase text-xs tracking-widest" />
        <select data-testid="discount-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-dark font-mono uppercase text-xs tracking-widest sm:!w-32">
          <option value="percent">% OFF</option>
          <option value="fixed">$ OFF</option>
        </select>
        <input data-testid="discount-value-input" required type="number" step="0.01" min="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="VALUE" className="input-dark font-mono uppercase text-xs tracking-widest sm:!w-28" />
        <button data-testid="discount-create-button" disabled={saving} className="btn-kinetic !py-2.5"><Plus size={14} /> Create</button>
      </form>
      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left font-mono text-xs">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="p-4">Code</th><th className="p-4">Discount</th><th className="p-4">Times Used</th><th className="p-4">Status</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-600 uppercase tracking-widest" data-testid="discounts-empty-row">No codes yet — make one for your next Kilgore event</td></tr>}
            {codes.map((c) => (
              <tr key={c.code} className="border-b border-white/5 hover:bg-cardhover transition-colors" data-testid={`discount-row-${c.code}`}>
                <td className="p-4 text-kinetic">{c.code}</td>
                <td className="p-4 text-white">{c.type === "percent" ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}</td>
                <td className="p-4 text-zinc-400">{c.times_used || 0}</td>
                <td className="p-4">
                  <button data-testid={`discount-toggle-${c.code}`} onClick={() => toggle(c.code)} className={`px-3 py-1.5 border uppercase text-[11px] tracking-widest transition-colors ${c.active ? "border-emerald-500/50 text-emerald-500" : "border-white/15 text-zinc-500"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="p-4 text-right">
                  <button data-testid={`discount-delete-${c.code}`} onClick={() => remove(c.code)} className="text-zinc-400 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LookbookAdmin() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ image: "", caption: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/lookbook").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/lookbook", form);
      toast.success("Photo added to lookbook");
      setForm({ image: "", caption: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/admin/lookbook/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading lookbook…</p>;
  return (
    <div data-testid="admin-lookbook">
      <form onSubmit={create} className="border border-white/10 bg-card p-5 mb-6 grid sm:grid-cols-[2fr_1fr_auto] gap-3">
        <input data-testid="lookbook-image-input" required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="IMAGE URL" className="input-dark font-mono text-xs" />
        <input data-testid="lookbook-caption-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="CAPTION" className="input-dark font-mono uppercase text-xs tracking-widest" />
        <button data-testid="lookbook-add-button" disabled={saving} className="btn-kinetic !py-2.5"><Plus size={14} /> Add photo</button>
      </form>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.length === 0 && <p className="font-mono text-xs text-zinc-600 uppercase tracking-widest col-span-full py-8 text-center">No photos yet — add your first Kilgore shot</p>}
        {items.map((it) => (
          <div key={it.id} className="relative group border border-white/10 bg-card" data-testid={`lookbook-admin-item-${it.id}`}>
            <img src={it.image} alt={it.caption} className="w-full aspect-square object-cover" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 p-2 truncate">{it.caption || "—"}</p>
            <button data-testid={`lookbook-delete-${it.id}`} onClick={() => remove(it.id)} className="absolute top-2 right-2 bg-black/70 border border-white/10 p-1.5 text-zinc-400 hover:text-red-500 transition-colors">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Customers() {
  const [customers, setCustomers] = useState(null);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!customers) return <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading customers…</p>;
  return (
    <div className="border border-white/10 bg-card overflow-x-auto" data-testid="admin-customers-table">
      <table className="w-full text-left font-mono text-xs">
        <thead>
          <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
            <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Type</th><th className="p-4">Orders</th><th className="p-4">Lifetime Spend</th>
          </tr>
        </thead>
        <tbody>
          {customers.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-600 uppercase tracking-widest">No customers yet</td></tr>}
          {customers.map((c) => (
            <tr key={c.email} className="border-b border-white/5 hover:bg-cardhover transition-colors" data-testid={`customer-row-${c.email}`}>
              <td className="p-4 text-zinc-200">{c.name || "—"}</td>
              <td className="p-4 text-zinc-400">{c.email}</td>
              <td className="p-4">{c.registered ? <span className="text-emerald-500">REGISTERED</span> : <span className="text-zinc-500">GUEST</span>}</td>
              <td className="p-4 text-zinc-300">{c.orders}</td>
              <td className="p-4 text-white">${c.spent.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  const content = useMemo(() => {
    if (tab === "Overview") return <Overview />;
    if (tab === "Orders") return <Orders />;
    if (tab === "Products") return <Products />;
    if (tab === "Inventory") return <Inventory />;
    if (tab === "Discounts") return <Discounts />;
    if (tab === "Lookbook") return <LookbookAdmin />;
    return <Customers />;
  }, [tab]);

  if (!user || user.role !== "admin") {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="admin-denied">
        <p className="font-mono text-xs uppercase tracking-widest text-red-500">Admin access only.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 pt-28 pb-16" data-testid="admin-dashboard">
      <p className="eyebrow mb-3">Coleman Customs // Ops Console</p>
      <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Run the shop</h1>
      <div className="flex flex-wrap gap-2 mt-10 mb-8" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`font-mono text-xs uppercase tracking-widest px-5 py-2.5 border transition-colors active:scale-95 ${
              tab === t ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-400 hover:border-kinetic hover:text-kinetic"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {content}
    </div>
  );
}
